<template>
    <div class="academy-activities-layout" :key="key">
        <b-overlay 
            :show="isLoading" 
            rounded="sm" 
            opacity="0.6" 
            spinner-small 
            spinner-variant="primary"
        >
            <!-- Navbar superior -->
            <nav class="custom-nav">
                <div class="d-flex justify-content-between w-100">
                    <!-- Rectángulo izquierdo -->
                    <div id="left-rectangle" class="left-rectangle">
                        <div @click="navigateToHome" class="navigate">
                            <img src="@/assets/imgs/logoDark.svg" class="img-fluid" alt="Logo Inventech">
                        </div>
                        <div class="separator">|</div>
                        <span class="text-black">{{ leftTitle }}</span>
                        <b-badge 
                            v-if="isReadOnly" 
                            variant="info" 
                            class="ml-2"
                        >
                            <b-icon-eye></b-icon-eye> {{ $t('academy.read_only') }}
                        </b-badge>
                        <div class="separator">|</div>
                        <div
                            class="navigate"
                            @click="duplicateProject"
                            v-b-tooltip.hover.top="$t('home.duplicate-project')"
                        >
                            <img src="@/assets/imgs/iconDuplicated.svg" alt="icon-duplicated" width="24">
                        </div>
                        <div class="separator">|</div>
                        <!-- BOTÓN UNDO -->
                        <div
                            @click="triggerUndo"
                            v-b-tooltip.hover.buttom="$t('workspace.tooltip.back')"
                            :class="{ 'disabled-icon': !canUndo }"
                            class="undo-icon"
                        >
                            <img src="@/assets/imgs/undo.png" alt="Undo icon" width="24">
                        </div>
                        <!-- BOTÓN REDO -->
                        <div
                            @click="triggerRedo"
                            v-b-tooltip.hover.buttom="$t('workspace.tooltip.forward')"
                            class="icon-flipped"
                            :class="{ 'disabled-icon': !canRedo }"
                        >
                            <img src="@/assets/imgs/undo.png" alt="Redo icon" width="24">
                        </div>
                       <!--  <div class="separator">|</div>
                        <SaveStatusIndicator :status="saveStatus" /> -->
                    </div>

                    <!-- Rectángulo derecho -->
                    <div id="right-rectangle" class="right-rectangle">
                        <template v-if="currentLessonActivities.length > 0">
                            <div class="navigation-arrows d-flex align-items-center">
                                <b-button
                                    variant="link"
                                    class="nav-button"
                                    :disabled="!hasPreviousActivity"
                                    @click="navigateToPreviousActivity"
                                >
                                    <img 
                                        src="@/assets/icons/academyIcons/39.png" 
                                        alt="Previous" 
                                        width="24"
                                        class="nav-icon"
                                    >
                                </b-button>
                                <span class="activity-counter">
                                    {{ currentActivityNumber }}/{{ totalActivitiesInLesson }}
                                </span>
                                <b-button
                                    variant="link"
                                    class="nav-button"
                                    :disabled="!hasNextActivity"
                                    @click="navigateToNextActivity"
                                >
                                    <img 
                                        src="@/assets/icons/academyIcons/40.png" 
                                        alt="Next" 
                                        width="24"
                                        class="nav-icon"
                                    >
                                </b-button>
                            </div>
                        </template>
                        <span v-if="showActionButton" class="status-text me-2">Status:</span>
                        <b-button
                            v-if="showActionButton"
                            :variant="getActionButtonVariant"
                            :disabled="isActionButtonDisabled"
                            @click="handleActionButton"
                            size="sm"
                            class="action-button me-2"
                        >
                            {{ getActionButtonText }}
                        </b-button>
                        <div class="separator">|</div>
                        <b-dropdown
                            id="help-dropdown"
                            size="sm"
                            variant="link"
                            toggle-class="text-decoration-none dropdown-lang p-0"
                            no-caret
                            right
                            @show="$root.$emit('bv::hide::tooltip', 'help-dropdown-btn')"
                        >
                            <template #button-content>
                            <img
                                id="help-dropdown-btn"
                                src="@/assets/imgs/help-icon.png"
                                class="img-fluid mt-0.5"
                                alt="Help icon"
                                width="23"
                            >
                            </template>
                            <b-dropdown-item-button v-b-modal.modalShorcuts>{{ $t('workspace.help.diagram_shortcuts') }}</b-dropdown-item-button>
                            <b-dropdown-item-button @click.prevent="launchTour">{{ $t('workspace.help.take_a_tour') }}</b-dropdown-item-button>
                            <b-dropdown-item href="https://forms.gle/tpWeVrBJPNkpixnG7" target="_blank">{{ $t('workspace.help.leave_feedback') }}</b-dropdown-item>
                        </b-dropdown>

                        <!-- Tooltip -->
                        <b-tooltip target="help-dropdown-btn" placement="buttom">
                            Ayuda
                        </b-tooltip>
                        <b-modal
                          id="modalShorcuts"
                          :title="$t('workspace.shortcuts.title')"
                          hide-footer
                          ok-only
                        >
                            <div class="d-flex justify-content-center">
                                <ShorcutsComponent />
                            </div>
                        </b-modal>
                        <b-dropdown size="sm" variant="link" toggle-class="text-decoration-none dropdown-lang p-0" no-caret>
                            <template #button-content>
                                <img src="@/assets/imgs/iconoIdiomasDark.svg" class="img-fluid mt-0.5" alt="Lang icon" width="23">
                            </template>
                            <b-dropdown-item-button @click="setLocale('pt')">PT</b-dropdown-item-button>
                            <b-dropdown-item-button @click="setLocale('es')">ES</b-dropdown-item-button>
                            <b-dropdown-item-button @click="setLocale('en')">EN</b-dropdown-item-button>
                        </b-dropdown>
                        <div class="navbar-btn">
                            <UserProfile v-if="isAuthenticated"/>
                        </div>
                    </div>
                </div>
            </nav>

            <!-- Contenido principal -->
            <div class="main-content">
                <slot></slot>
            </div>

            <!-- Slot para los botones flotantes -->
            <slot name="floating-buttons"></slot>

            <!-- Contenedor para ActionButtons y KeyboardAccessControl -->
            <div class="action-buttons-container">
                <KeyboardAccessControl 
                    v-if="currentGroupData"
                    :groupId="currentGroupId || $route.query.groupId"
                    :students="currentGroupData.students || []"
                />
            </div>

            <!-- Sidebar derecho con su botón de expansión -->
            <div id="right-sidebar-instructions" class="right-sidebar-wrapper">
                <div class="right-sidebar" :class="{ 'sidebar-expanded': isSidebarExpanded, 'sidebar-wide': isSidebarWide}">
                    <div 
                        id="sidebar-btn-instructions"
                        v-b-tooltip.hover.top="isSidebarExpanded ? $t('academy.hide-guide') : $t('academy.view-guide')"
                        class="sidebar-toggle" 
                        :class="{ 'active': isSidebarExpanded && !showFeedback }"
                        @click="openSidebarInstructions"
                    >
                        <img src="@/assets/icons/academyIcons/38.png" alt="Expand" width="28">
                    </div>
                    <div 
                        v-if="showFeedbackButton" 
                        class="sidebar-toggle feedback-toggle" 
                        :class="{ 'active': isSidebarExpanded && showFeedback }"
                        @click="toggleFeedback"
                    >
                        <img src="@/assets/icons/academyIcons/42.png" alt="Feedback" width="28">
                    </div>
                    <div class="sidebar-content">
                        <div class="sidebar-header">
                            <b-row class="align-items-center">
                                <b-col class="d-flex justify-content-center p-0">
                                    <h4 class="w-100 text-center" style="margin-left: 32px;">{{ steps[effectiveCurrentStep]?.stepName }}</h4>
                                </b-col>
                                <b-col cols="auto" class="d-flex justify-content-end align-items-center gap-2">
                                    <ChangeIcon
                                        v-if="!isStudent"
                                        v-b-tooltip.hover.top="isToggledIcon ? $t('academy.read-mode') : $t('academy.edit-guide')"
                                        :model-value="isToggledIcon"
                                        @update:modelValue="handleToggle"
                                        iconA="pen"
                                        iconB="eye"
                                        class="px-1"
                                        icon-color="white"
                                        hover-color="black"
                                    >
                                        <template #inactive></template>
                                        <template #active></template>
                                    </ChangeIcon>
                                    <ChangeIcon
                                        v-b-tooltip.hover.top="isSidebarWide ? $t('workspace.programming.sidebar.btn-collapse') : $t('workspace.programming.sidebar.btn-expand')"
                                        :model-value="isSidebarWide"
                                        @update:modelValue="isSidebarWide = $event"
                                        iconA="arrows-fullscreen"
                                        iconB="arrows-angle-contract"
                                        class="px-1"
                                    >
                                        <template #inactive></template>
                                        <template #active></template>
                                    </ChangeIcon>
                                    <b-button
                                        v-if="isSidebarExpanded"
                                        v-b-tooltip.hover.top="$t('academy.close')"    
                                        variant="link"
                                        class="close-sidebar-btn px-1"
                                        @click="openSidebarInstructions"
                                        :aria-label="$t('academy.close')"
                                    >
                                        <b-icon-x-lg></b-icon-x-lg>
                                    </b-button>
                                </b-col>
                            </b-row>
                        </div>
                        <div class="sidebar-body">
                            <div v-if="showFeedback" class="feedback-section">
                                <h5>{{ $t('academy.grading-and-feedback') }}</h5>
                                <div class="form-group">
                                    <label for="score">{{ $t('academy.grading') }}</label>
                                    <input 
                                        v-if="isTutor"
                                        type="number" 
                                        id="score" 
                                        v-model="feedbackData.score"
                                        class="form-control" 
                                        min="0" 
                                        max="100" 
                                        :placeholder="$t('academy.enter-grade')"
                                    >
                                    <div v-else class="score-display">
                                        {{ currentLessonActivities[currentActivityIndex]?.score || 0 }} / 100
                                    </div>
                                </div>
                                <div class="form-group">
                                    <label for="feedback">{{ $t('academy.observations') }}</label>
                                    <textarea 
                                        v-if="isTutor"
                                        id="feedback" 
                                        v-model="feedbackData.feedback"
                                        class="form-control" 
                                        :placeholder="$t('academy.enter-observations')"
                                        rows="4"
                                    ></textarea>
                                    <div v-else class="feedback-display">
                                        {{ currentLessonActivities[currentActivityIndex]?.feedback || this.$t('academy.no_observations') }}
                                    </div>
                                </div>
                                <div v-if="isTutor" class="feedback-actions">
                                    <b-button 
                                        variant="primary" 
                                        @click="saveFeedback"
                                        :disabled="!isValidFeedback"
                                    >
                                       {{ $t('academy.save')}}
                                    </b-button>
                                    <b-button 
                                        variant="secondary" 
                                        @click="cancelFeedback"
                                    >
                                        {{ $t('academy.cancel') }}
                                    </b-button>
                                </div>
                            </div>
                            <slot v-else name="sidebar-content" :is-sidebar-wide="isSidebarWide"></slot>
                            <div v-if="isPreview">
                            <!-- Footer con paginador -->
                            <div v-if="isPreview">
  <div class="sidebar-footer-paginator">
    <div class="circle-buttons">
      <button
        v-for="(step, idx) in steps"
        :key="idx"
        @click="goToStep(idx)"
        class="circle-btn"
        :class="{ active: effectiveCurrentStep === idx }"
      >
        <span class="circle-label">{{ idx + 1 }}</span>
        <span class="circle-hover">{{ step.stepName }}</span>
      </button>
    </div>
  </div>
</div>


                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </b-overlay>
    </div>
</template>

<script>
import UserProfile from '@/views/user/UserProfile.vue';
import ShorcutsComponent from '@/components/ShorcutsComponent.vue';
import KeyboardAccessControl from '@/components/KeyboardAccessControl.vue';
import { mapGetters, mapActions, mapMutations, mapState } from 'vuex';
import Swal from "sweetalert2";
import practicalGuideData from "@/data/practicalGuideData";
import academyData from "@/data/academyData";
import { doc, getDoc, getDocs, collection, updateDoc } from 'firebase/firestore';
import { db } from '@/data/firebaseConfig';
import { getAuth } from 'firebase/auth';
import ChangeIcon from '@/components/changeIcon.vue'
import { EventBus } from '@/main';
import { BIconEye } from 'bootstrap-vue';
import { startTour } from '@/helpers/onDriverTour';
import SaveStatusIndicator from '@/components/SaveStatusIndicator.vue';
export default {
    name: 'AcademyActivitiesLayout',
    components: {
        UserProfile,
        KeyboardAccessControl,
        ChangeIcon,
        BIconEye,
        ShorcutsComponent,
        SaveStatusIndicator
    },
    props: {
        stepName:{
            type: String,
            default: ''
        },
        isPreview: {
            type: Boolean,
            default: false
        },
        steps: {
            type: Array,
            default: () => []
        },
        isToggledIcon:{
            type: Boolean,
            default: false
        },
        leftTitle: {
            type: String,
            default: ''
        },
        rightSecondaryText: {
            type: String,
            default: ''
        },
        currentActivityId: {
            type: String,
            default: null
        },
        currentGroupId: {
            type: String,
            default: null
        },
        currentView: {
            type: String,
            default: null
        },
        isReadOnly: {
            type: Boolean,
            default: false
        },
        externalCurrentStep: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            isSidebarWide: false,
            isLoading: false,
            currentLesson: null,
            currentModuleId: null,
            currentLessonActivities: [],
            currentActivityIndex: -1,
            currentLessonId: null,
            key: 0,
            originalLessonActivities: [],
            authReady: false,
            currentGroupData: null,
            feedbackData: {
                score: null,
                feedback: ''
            },
            canUndo: false, 
            canRedo: false,
            currentStep: 0
        };
    },
    computed: {
        ...mapState(['isSidebarExpanded', 'showFeedback']),
        ...mapGetters('auth', ['isAuthenticated', 'user']),
        ...mapGetters('projects', ['selectedProject', 'saveStatus']),
        isStudent() {
            return this.$route.query.isAdmin !== 'true';
        },
        canGradeGroup() {
            return this.currentActivityId && this.currentGroupId;
        },
        hasNextActivity() {
            return this.currentActivityIndex < (this.currentLessonActivities.length - 1);
        },
        hasPreviousActivity() {
            return this.currentActivityIndex > 0;
        },
        currentActivityNumber() {
            return this.currentActivityIndex + 1;
        },
        totalActivitiesInLesson() {
            if (!this.currentActivityId) return 0;
            if (this.originalLessonActivities?.length > 0) {
                return this.originalLessonActivities.length;
            }
            return 0;
        },
        isCurrentStudent() {
            const currentUserUid = this.user?.uid;
            const studentUidFromUrl = this.$route.query.studentUid;
            return currentUserUid && studentUidFromUrl && currentUserUid === studentUidFromUrl;
        },
        showActionButton() {
            return this.$route.query.groupId && this.$route.query.studentUid;
        },
        isActionButtonDisabled() {
            const currentActivity = this.currentLessonActivities[this.currentActivityIndex];
            if (!currentActivity) return true;

            if (this.isCurrentStudent) {
                return currentActivity.status !== 'created';
            } else {
                return currentActivity.status !== 'sent';
            }
        },
        getActionButtonText() {
            const currentActivity = this.currentLessonActivities[this.currentActivityIndex];
            if (!currentActivity) return '';

            if (this.isCurrentStudent) {
                switch (currentActivity.status) {
                    case 'created': return this.$t('academy.btn-send');
                    case 'sent': return this.$t('academy.sent');
                    case 'calified': return this.$t('academy.graded_status');
                    default: return '';
                }
            } else {
                switch (currentActivity.status) {
                    case 'created': return this.$t('academy.not-received');
                    case 'sent': return this.$t('academy.grade');;
                    case 'calified': return this.$t('academy.graded_status');
                    default: return '';
                }
            }
        },
        getActionButtonVariant() {
            const currentActivity = this.currentLessonActivities[this.currentActivityIndex];
            if (!currentActivity) return 'secondary';

            if (currentActivity.status === 'calified') {
                return 'success';
            }

            if (this.isCurrentStudent) {
                return currentActivity.status === 'created' ? 'primary' : 'secondary';
            } else {
                return currentActivity.status === 'sent' ? 'success' : 'secondary';
            }
        },
        isTutor() {
            const currentActivity = this.currentLessonActivities[this.currentActivityIndex];
            const isTutorByRole = this.user?.role === 'tutor';
            const isMentor = currentActivity?.mentorUid === this.user?.uid;
            const canGrade = currentActivity?.status === 'sent' || currentActivity?.status === 'calified';
            return (isTutorByRole || isMentor) && canGrade;
        },
        isGroupActivity() {
            return this.$route.query.groupId && this.$route.query.studentUid;
        },
        isValidFeedback() {
            return this.feedbackData.score !== null && 
                   this.feedbackData.score >= 0 && 
                   this.feedbackData.score <= 100;
        },
        showFeedbackButton() {
            const currentActivity = this.currentLessonActivities[this.currentActivityIndex];
            if (!currentActivity) return false;

            // Para tutores: mostrar si está enviada o calificada
            if (this.isTutor) {
                return currentActivity.status === 'sent' || currentActivity.status === 'calified';
            }
            
            // Para estudiantes: mostrar solo si está calificada
            return currentActivity.status === 'calified';
        },
        effectiveCurrentStep() {
            return this.externalCurrentStep !== null ? this.externalCurrentStep : this.currentStep;
        }
    },
    watch: {
        externalCurrentStep: {
            immediate: true,
            handler(newValue) {
                if (newValue !== null && newValue !== this.currentStep) {
                    this.currentStep = newValue;
                }
            }
        },
        '$route.params.activityId': {
            immediate: true,
            async handler(newActivityId) {
                if (newActivityId) {
                    this.isLoading = true;
                    try {
                        await this.loadActivitiesForGuide(newActivityId);
                    } catch (error) {
                        console.error('Error al cargar la actividad:', error);
                    } finally {
                        this.isLoading = false;
                    }
                }
            }
        },
        '$route.query.studentUid': {
            immediate: true,
            handler(newStudentUid) {
                if (newStudentUid) {
                    this.$forceUpdate();
                }
            }
        },
        authReady: {
            immediate: true,
            handler(newValue) {
                if (newValue) {
                    this.$forceUpdate();
                }
            }
        },
        '$route.query.groupId': {
            immediate: true,
            async handler(newGroupId) {
                if (newGroupId) {
                    try {
                        const groupRef = doc(db, 'groups', newGroupId);
                        const groupDoc = await getDoc(groupRef);
                        if (groupDoc.exists()) {
                            this.currentGroupData = groupDoc.data();
                        }
                    } catch (error) {
                        console.error('Error al cargar el grupo:', error);
                    }
                }
            }
        }
    },
    methods: {
        ...mapMutations([
            'toggleSidebar', 
            'selectButton', 
            'SET_SIDEBAR_EXPANDED', 
            'SET_SHOW_FEEDBACK', 
            'toggleSidebarInstructions'
        ]),
        ...mapActions('projects', [
            'duplicateProjectWithData', 
            'deselectProject', 
            'fetchProjectById',
            'createProject',
            'updateProject',
            'fetchProjects'
        ]),
        goToStep(idx) {
        if (this.externalCurrentStep !== null) {
            // Si está siendo controlado externamente, emitir al padre
            this.$emit('step-changed', idx);
        } else {
            // Si es controlado internamente, cambiar directamente
            this.currentStep = idx;
        }
    },

        handleToggle(newValue) {
            this.$emit('update:is-toggled-icon', newValue);
        },

        setLocale(locale) {
            if (this.$i18n.locale !== locale) {
                this.$i18n.locale = locale;
                this.$router.push({
                    params: { ...this.$route.params, lang: locale }, 
                    query: this.$route.query,
                }).catch(err => {
                    if (err.name !== 'NavigationDuplicated') {
                        throw err;
                    }
                });
            }
        },
        navigateToHome() {
            if (this.$route.path !== `/${this.$i18n.locale}/home`) {
                this.$router.push(`/${this.$i18n.locale}/home`);
            }
        },
        async duplicateProject() {
    if (this.selectedProject) {
        try {
            this.isLoading = true;

            const isPracticalGuide = this.selectedProject.templateInfo && 
                                    !this.selectedProject.userId && 
                                    this.selectedProject.id?.startsWith('activity');

            let newProjectId;

            if (isPracticalGuide) {                
                const guideName = this.selectedProject.name || 'Guía práctica sin título';
                const projectData = this.selectedProject.project || {};
                
                const newProjectData = {
                    name: `${guideName} - Copia`,
                    category: 'activity',
                    imageUrl: this.selectedProject.imageUrl || '',
                    isActivity: true,
                    components: this.selectedProject.components || [],
                    connections: this.selectedProject.connections || [],
                    code: this.selectedProject.code || '',
                    templateInfo: {
                        name: this.selectedProject.name || 'Guía práctica',
                        description: this.selectedProject.InitialProjectDescription || '',
                        introduction: this.selectedProject.introduction || [],
                        code: projectData.code || this.selectedProject.code || '',
                        titleIntroduction: this.selectedProject.titleIntroduction || '',
                        steps: this.selectedProject.steps || [],
                        difficulty: this.selectedProject.difficulty || 'beginner',
                        estimatedTime: this.selectedProject.estimatedTime || 60,
                        tags: this.selectedProject.tags || []
                    }
                };

                newProjectId = await this.createProject(newProjectData);

                const updateData = {
                    components: this.selectedProject.components || [],
                    connections: this.selectedProject.connections || [],
                    code: projectData.code || this.selectedProject.code || '',
                    updatedAt: new Date().toISOString()
                };

                console.log("[DEBUG] Actualizando con:", {
                    componentsCount: updateData.components.length,
                    connectionsCount: updateData.connections.length,
                    hasCode: !!updateData.code
                });

                await this.updateProject({ 
                    projectId: newProjectId, 
                    projectData: updateData 
                });
            } else {
                newProjectId = await this.duplicateProjectWithData(this.selectedProject);
            }

            if (newProjectId) {
                
                const newRoute = `/${this.$i18n.locale}/board/${newProjectId}`;
                
                const routeData = this.$router.resolve({
                    path: newRoute
                });
                window.open(routeData.href, '_blank');
                
                Swal.fire({
                    icon: 'success',
                    title: this.$t('academy.success'),
                    text: this.$t('academy.duplicate_activity_success'),
                    timer: 2000,
                    showConfirmButton: false
                });
            }

        } catch (error) {
            console.error('Error al duplicar:', error);
            Swal.fire({
                icon: 'error', 
                title: 'Error',
                text: `${this.$t('academy.duplicate_activity_error')}: ${error.message}`
            });
        } finally {
            this.isLoading = false;
        }
    }
},
        openSidebarInstructions() {
            this.toggleSidebarInstructions();
            this.$nextTick(() => {
                this.$root.$emit('bv::hide::tooltip');
                this.$root.$emit('bv::refresh::tooltip');
            });
        },
        toggleFeedback() {
            const currentActivity = this.currentLessonActivities[this.currentActivityIndex];
            
            if (currentActivity?.status === 'created') {
                Swal.fire({
                    icon: 'info',
                    title: this.$t('academy.not-available'),
                    text: this.$t('academy.cannot-grade-unsubmitted'),
                    confirmButtonColor: '#76b8f1'
                });
                return;
            }

            if (this.isSidebarExpanded && !this.showFeedback) {
                this.SET_SHOW_FEEDBACK(true);
            } else {
                this.SET_SIDEBAR_EXPANDED(!this.isSidebarExpanded);
                if (this.isSidebarExpanded) {
                    this.SET_SHOW_FEEDBACK(true);
                    if (currentActivity) {
                        this.feedbackData = {
                            score: currentActivity.score || null,
                            feedback: currentActivity.feedback || ''
                        };
                    }
                }
            }
        },
        async saveFeedback() {
            if (!this.isValidFeedback) return;
            
            try {
                const currentActivity = this.currentLessonActivities[this.currentActivityIndex];
                const { groupId, studentUid } = this.$route.query;
                const activityId = this.$route.params.activityId;
                
                if (!groupId || !studentUid || !activityId) {
                    throw new Error('Faltan parámetros necesarios');
                }
                
                currentActivity.status = 'calified';
                currentActivity.score = this.feedbackData.score;
                currentActivity.feedback = this.feedbackData.feedback;
                this.$forceUpdate();

                const groupRef = doc(db, 'groups', groupId);
                const groupDoc = await getDoc(groupRef);
                
                if (groupDoc.exists()) {
                    const activities = groupDoc.data().activities || [];
                    const activityIndex = activities.findIndex(
                        act => act.projectUid === activityId && act.studentUid === studentUid
                    );
                    
                    if (activityIndex !== -1) {
                        activities[activityIndex] = {
                            ...activities[activityIndex],
                            status: 'calified',
                            score: this.feedbackData.score,
                            feedback: this.feedbackData.feedback,
                            completedAt: new Date().toISOString()
                        };
                        
                        await updateDoc(groupRef, { activities });
                        
                        Swal.fire({
                            icon: 'success',
                            title: this.$t('academy.saved'),
                            text: this.$t('academy.grade-and-feedback-saved'),
                            timer: 1500,
                            showConfirmButton: false
                        });

                         this.SET_SHOW_FEEDBACK(false)
                    } else {
                        throw new Error('No se encontró la actividad en el grupo');
                    }
                } else {
                    throw new Error('No se encontró el grupo');
                }
            } catch (error) {
                console.error('Error al guardar la calificación y feedback:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: this.$t('academy.save-error')
                });
            }
        },
        cancelFeedback() {
            this.SET_SHOW_FEEDBACK(false)
            this.feedbackData = {
                score: null,
                feedback: ''
            };
        },
        navigateToPage(route) {
            // Si estamos en la misma ruta, no hacemos nada
            if (this.$route.name === route) return;

            const navigationParams = {
                params: { 
                    lang: this.$i18n.locale,
                    activityId: this.currentActivityId 
                }
            };

            // Si hay un groupId, lo incluimos en la query
            if (this.currentGroupId) {
                navigationParams.query = { groupId: this.currentGroupId };
            }

            switch(route) {
                case 'activity-solved':
                    this.$router.push({
                        name: 'activity-solved',
                        ...navigationParams
                    }).catch(err => {
                        if (err.name !== 'NavigationDuplicated') {
                            throw err;
                        }
                    });
                    break;

                case 'class-activity':
                    this.$router.push({
                        name: 'class-activity',
                        ...navigationParams
                    }).catch(err => {
                        if (err.name !== 'NavigationDuplicated') {
                            throw err;
                        }
                    });
                    break;

                case 'grade-group':
                    this.$router.push({
                        name: 'grade-activity',
                        ...navigationParams
                    }).catch(err => {
                        if (err.name !== 'NavigationDuplicated') {
                            throw err;
                        }
                    });
                    break;
            }
        },
        async loadActivitiesForGuide(activityId) {
            const groupId = this.$route.query.groupId;
            const studentUid = this.$route.query.studentUid || (this.$store.state.auth?.user?.uid);

            if (groupId && studentUid) {
                try {
                    const groupRef = doc(db, 'groups', groupId);
                    const groupSnap = await getDoc(groupRef);

                    if (groupSnap.exists()) {
                        const groupData = groupSnap.data();
                        this.currentGroupData = groupData;

                        const studentActivities = (groupData.activities || []).filter(
                            act => act.studentUid === studentUid
                        );

                        const foundActivity = studentActivities.find(
                            act => act.projectUid === activityId
                        );

                        if (foundActivity) {
                            // Filtrar solo las actividades de la misma lección
                            const lessonNumber = foundActivity.lessonNumber;
                            const lessonActivities = studentActivities.filter(
                                act => act.lessonNumber === lessonNumber
                            );

                            this.currentLessonActivities = lessonActivities;
                            // Ordenar por completedAt o por otro campo si lo prefieres
                            this.currentLessonActivities.sort((a, b) => {
                                return new Date(a.completedAt) - new Date(b.completedAt);
                            });
                            this.currentActivityIndex = this.currentLessonActivities.findIndex(
                                act => act.projectUid === activityId
                            );

                            // Buscar las actividades originales en el curso
                            try {
                                const courses = await academyData.getCourses();
                                for (const course of courses) {
                                    for (const module of course.modules) {
                                        for (const lesson of module.lessons) {
                                            // Buscar la lección que coincida con el lessonNumber
                                            if (module.number === lessonNumber) {
                                                this.originalLessonActivities = lesson.classActivities;
                                                break;
                                            }
                                        }
                                    }
                                }
                            } catch (error) {
                                console.error('[DEBUG] Error al buscar actividades originales:', error);
                            }

                            // Asegurarnos de que el proyecto se cargue
                            try {
                                await this.fetchProjectById(activityId);
                                this.$root.$emit('reload-canvas');
                                this.$root.$emit('reload-project');
                                await this.reloadProject(activityId);
                            } catch (error) {
                                console.error('[DEBUG] Error al cargar el proyecto:', error);
                            }

                            return;
                        }
                    }
                } catch (error) {
                    console.error('Error al cargar el grupo:', error);
                }
            }

            // 2. Si no es una actividad de grupo, usar el flujo de templates
            try {
                // 1. Obtener el curso completo
                const courses = await academyData.getCourses();

                // 2. Buscar la actividad en todos los cursos
                let foundActivity = null;
                let foundModule = null;
                let foundLesson = null;

                for (const course of courses) {
                    for (const module of course.modules) {
                        for (const lesson of module.lessons) {
                            const activity = lesson.classActivities.find(act => act.id === activityId);
                            if (activity) {
                                foundActivity = activity;
                                foundModule = module;
                                foundLesson = lesson;
                                break;
                            }
                        }
                        if (foundActivity) break;
                    }
                    if (foundActivity) break;
                }

                if (foundActivity) {
                    // 3. Obtener todas las actividades de la lección actual
                    this.currentLessonActivities = foundLesson.classActivities;
                    this.originalLessonActivities = foundLesson.classActivities;

                    // 4. Ordenar las actividades por número (si tienen)
                    this.currentLessonActivities.sort((a, b) => {
                        const aNumber = a.activityNumber || a.number || 0;
                        const bNumber = b.activityNumber || b.number || 0;
                        return aNumber - bNumber;
                    });

                    // 5. Actualizar el índice de la actividad actual
                    this.currentActivityIndex = this.currentLessonActivities.findIndex(
                        activity => activity.id === activityId
                    );

                    // Para templates, no intentamos cargar el proyecto ya que no existe
                    // Solo emitimos los eventos necesarios para la UI
                    this.$root.$emit('reload-canvas');
                    this.$root.$emit('reload-project');
                    
                }
            } catch (error) {
                console.error('[DEBUG] Error al cargar las actividades (template):', error);
            }
        },
        
        async navigateToNextActivity() {
            if (this.hasNextActivity) {
                const nextActivity = this.currentLessonActivities[this.currentActivityIndex + 1];
                const nextId = nextActivity.projectUid || nextActivity.id;
                console.log('Navegando a siguiente actividad:', nextActivity);
                await this.navigateToActivity(nextId);
            }
        },
        
        async navigateToPreviousActivity() {
            if (this.hasPreviousActivity) {
                const prevActivity = this.currentLessonActivities[this.currentActivityIndex - 1];
                const prevId = prevActivity.projectUid || prevActivity.id;
                console.log('Navegando a actividad anterior:', prevActivity);
                await this.navigateToActivity(prevId);
            }
        },
        
        async navigateToActivity(activityId) {
            try {
                this.isLoading = true;
                const currentUrl = new URL(window.location.href);
                currentUrl.pathname = currentUrl.pathname.replace(
                    this.$route.params.activityId,
                    activityId
                );
                window.location.href = currentUrl.href;
            } catch (error) {
                console.error('Error al navegar:', error);
                this.isLoading = false;
            }
        },
        
        async reloadProject(activityId) {
            try {
                // Deseleccionar el proyecto actual
                await this.deselectProject();
                
                // Obtener la guía actual
                const guides = await practicalGuideData.getAllGuides();
                const currentGuide = guides.find(guide => guide.id === activityId);
                
                if (currentGuide && currentGuide.projectId) {
                    // Cargar el nuevo proyecto
                    await this.fetchProjectById(currentGuide.projectId);
                    
                    // Forzar recarga del componente
                    this.key += 1;
                    
                    // Emitir eventos para recargar componentes
                    this.$root.$emit('reload-canvas');
                    this.$root.$emit('reload-project');
                    
                    // Forzar actualización del DOM
                    await this.$nextTick();
                } else {
                    // Si no encontramos la guía, intentar cargar el proyecto directamente
                    await this.fetchProjectById(activityId);
                    
                    // Forzar recarga del componente
                    this.key += 1;
                    
                    // Emitir eventos para recargar componentes
                    this.$root.$emit('reload-canvas');
                    this.$root.$emit('reload-project');
                    
                    // Forzar actualización del DOM
                    await this.$nextTick();
                }
            } catch (error) {
                console.error('Error al recargar el proyecto:', error);
            }
        },
        async handleActionButton() {
            const currentUserUid = this.user?.uid;
            const studentUidFromUrl = this.$route.query.studentUid;
            const groupId = this.$route.query.groupId;
            const activityId = this.$route.params.activityId;
            
            if (!currentUserUid || !studentUidFromUrl || !groupId || !activityId) return;
            
            const isStudent = currentUserUid === studentUidFromUrl;
            const currentActivity = this.currentLessonActivities[this.currentActivityIndex];
            
            if (!currentActivity) return;

            // Verificar el estado actual
            if (isStudent && currentActivity.status !== 'created') return;
            if (!isStudent && currentActivity.status !== 'sent') return;
            
            if (isStudent) {
                // Agregar confirmación antes de enviar
                const result = await Swal.fire({
                    title: this.$t('academy.are-you-sure'),
                    text: this.$t('academy.activity-submit-warning'),
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#76b8f1',
                    cancelButtonColor: '#d33',
                    confirmButtonText: this.$t('academy.confirm-send'),
                    cancelButtonText: this.$t('academy.cancel')
                });

                if (!result.isConfirmed) return;

                try {
                    // Referencia al documento del grupo
                    const groupRef = doc(db, 'groups', groupId);
                    const groupDoc = await getDoc(groupRef);
                    
                    if (groupDoc.exists()) {
                        const groupData = groupDoc.data();
                        const activities = groupData.activities || [];
                        
                        // Encontrar la actividad específica
                        const activityIndex = activities.findIndex(
                            act => act.projectUid === activityId && act.studentUid === studentUidFromUrl
                        );
                        
                        if (activityIndex !== -1) {
                            // Actualizar el estado de la actividad
                            activities[activityIndex] = {
                                ...activities[activityIndex],
                                status: 'sent',
                                sentAt: new Date().toISOString()
                            };
                            
                            // Actualizar el documento
                            await updateDoc(groupRef, { activities });
                            
                            // Actualizar el estado local
                            this.currentLessonActivities[this.currentActivityIndex].status = 'sent';
                            
                            Swal.fire({
                                icon: 'success',
                                title: this.$t('academy.sent-alert'),
                                text: this.$t('academy.submission-success'),
                                timer: 1500,
                                showConfirmButton: false
                            });
                        }
                    }
                } catch (error) {
                    console.error('Error al actualizar el estado:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: this.$t('academy.submission-error')
                    });
                }
            } else {
                // Lógica para tutores
                try {
                    const { value: score } = await Swal.fire({
                        title: this.$t('academy.grade_activity'),
                        input: 'number',
                        inputLabel: this.$t('academy.enter-grade') + ' (0-100)',
                        inputAttributes: {
                            min: 0,
                            max: 100,
                            step: 1
                        },
                        showCancelButton: true,
                        confirmButtonText: this.$t('academy.grade'),
                        cancelButtonText: this.$t('academy.cancel'),
                        inputValidator: (value) => {
                            if (!value) {
                                return this.$t('academy.grade_required');
                            }
                            if (value < 0 || value > 100) {
                                return this.$t('academy.grade_range_error');
                            }
                            return null;
                        }
                    });

                    if (score) {
                        // Actualizar el estado local inmediatamente
                        this.currentLessonActivities[this.currentActivityIndex].status = 'calified';
                        this.currentLessonActivities[this.currentActivityIndex].score = parseInt(score);
                        
                        // Forzar la actualización del componente
                        this.$forceUpdate();

                        const groupRef = doc(db, 'groups', groupId);
                        const groupDoc = await getDoc(groupRef);
                        
                        if (groupDoc.exists()) {
                            const groupData = groupDoc.data();
                            const activities = groupData.activities || [];
                            
                            const activityIndex = activities.findIndex(
                                act => act.projectUid === activityId && act.studentUid === studentUidFromUrl
                            );
                            
                            if (activityIndex !== -1) {
                                activities[activityIndex] = {
                                    ...activities[activityIndex],
                                    status: 'calified',
                                    score: parseInt(score),
                                    completedAt: new Date().toISOString()
                                };
                                
                                await updateDoc(groupRef, { activities });
                                
                                Swal.fire({
                                    icon: 'success',
                                    title: this.$t('academy.graded_status'),
                                    text: this.$t('academy.activity-graded-success'),
                                    timer: 1500,
                                    showConfirmButton: false
                                });
                            }
                        }
                    }
                } catch (error) {
                    // Revertir el estado local si hay error
                    this.currentLessonActivities[this.currentActivityIndex].status = 'sent';
                    this.currentLessonActivities[this.currentActivityIndex].score = null;
                    this.$forceUpdate();

                    console.error('Error al calificar la actividad:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: this.$t('academy.activity-graded-error')
                    });
                }
            }
        },
        triggerUndo() {
            if (this.canUndo) {
            console.log('Navbar emitiendo evento: do-undo');
            EventBus.$emit('do-undo');
            }
        },
        triggerRedo() {
            if (this.canRedo) {
            console.log('Navbar emitiendo evento: do-redo');
            EventBus.$emit('do-redo');
            }
        },

        // NUEVO: Este método RECIBE el estado y actualiza 'data'
        updateButtonStates(states) {
            this.canUndo = states.canUndo;
            this.canRedo = states.canRedo;
        },
        getTourType({ meta, query, name, isTutor }) {
            if (query.isAdmin === 'true') return 'admin';
            if (meta.isActivity) return isTutor ? 'tutor' : 'student';
            if (name === 'practical-guide-template' || meta.isPracticalGuide) return 'practical-guide';
            if (name === 'guide' || meta.isTemplate) return 'template';
            return 'user';
        },
        launchTour(){
           const tourType = this.getTourType({
                ...this.$route,
                isTutor: this.isTutor
            });

            startTour({ context: this, tourType });
        }
    },
    created() {
        if (this.$route.params.activityId) {
            this.SET_SIDEBAR_EXPANDED(true)
        }
        this.$store.watch(
            state => state.auth.user,
            () => {
                this.authReady = true;
                this.$forceUpdate();
            },
            { immediate: true }
        );
        EventBus.$on('update-history-state', this.updateButtonStates);
    },
    beforeDestroy() {
      EventBus.$off('update-history-state', this.updateButtonStates);
    },
    mounted() {
        const auth = getAuth();
    }
};
</script>

<style scoped>
.step-progress {
    margin-top: 1rem;
}
.markdown-preview{
code {
    background-color: rgb(238, 238, 238);
    border-radius: 0.4rem;
    color: rgb(0, 0, 0);
    font-size: 0.85rem;
    padding: 0.25em 0.3em;
  }

  img {
    display: inline-block;
    height: auto;
    margin-right: 0.5rem;
    max-width: 100%;

    &.ProseMirror-selectednode {
      outline: 1px solid rgba(210, 210, 210, 0.797);
    }
  }

  pre {
    background: rgb(238, 238, 238);
    border-radius: 0.5rem;
    border: 1px solid rgb(202, 202, 202);
    color: rgb(0, 0, 0);
    font-family: 'JetBrainsMono', monospace;
    margin: 1.5rem 0;
    padding: 0.75rem 1rem;

    code {
      background: none;
      color: inherit;
      font-size: 0.8rem;
      padding: 0;
    }
  }
}

h5 {
    color: #333;
    margin-bottom: 1rem;
}

p {
    color: #666;
    line-height: 1.5;
}
.sidebar-footer-paginator {
    position: absolute;
    top: auto;
    bottom: 0; 
    left: 0;
    right: 0;
    width: 100%;
    background: #ffffff;
    border-top: 1px solid #eee;
    padding: 0.75rem 0;
    display: flex;
    justify-content: center;
    z-index: 20;
    box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.042);
    border-radius: 0 0 0.5rem 0.5rem;
}
.circle-buttons {
  display: flex;
  gap: 0.5rem;
}

.circle-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #ccc;
  background: #f7f7f7;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.circle-label {
  transition: opacity 0.2s ease;
}

.circle-hover {
  position: absolute;
  bottom: 130%; 
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: #fff;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

.circle-hover::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px;
  border-style: solid;
  border-color: #333 transparent transparent transparent;
}

/* mostrar texto en hover */
.circle-btn:hover .circle-hover {
  opacity: 1;
}

/* estado activo */
.circle-btn.active {
  background: #007bff;
  color: #fff;
  border-color: #007bff;
}



.academy-activities-layout {
    position: relative;
}

.custom-nav {
padding-left: 0;
padding-right: 2.4rem;
display: flex;
justify-content: space-between;
align-items: flex-end;
margin-top: 15px;
}

.right-rectangle {
    background-color: white;
    border-radius: 0.5rem;
    padding-left: 1rem;
    display: flex;
    align-items: center;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    gap: 1rem;
}

  .left-rectangle {
    background-color: white;
    border-radius: 0.5rem;
    padding: 0.5rem 1rem;
    display: flex;
    align-items: center;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    min-width: 300px;
    height: 3rem;
  }

.left-rectangle {
    min-width: 300px;
}

.separator {
    color: #232323;
    font-weight: 500;
}

.navigate {
    cursor: pointer;
}

.navbar-btn {
    height: 100%;
    display: flex;
    align-items: center;
    margin-left: 0.5rem;
}

.main-content {
    margin-right: 24px;
    height: calc(100vh - 4.1rem);
    overflow: hidden;
}

.floating-button {
    position: fixed;
    bottom: 30px;
    left: 30px;
    z-index: 1000;
}

.floating-button ::v-deep .btn-primary {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2);
    background-color: #76b8f1;
    border: none;
    padding: 0;
}

.floating-button ::v-deep .floating-menu {
    left: 100% !important;
    right: auto !important;
    margin-left: 15px;
    min-width: 220px;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(0, 0, 0, 0.1);
    transform-origin: left top;
    padding: 0.25rem 0;
}

.floating-button ::v-deep .dropdown-item {
    padding: 0.35rem 1rem;
    color: #232323;
    transition: all 0.2s ease;
}

.floating-button ::v-deep .dropdown-item:hover {
    background-color: #f8f9fa;
    transform: translateX(5px);
}

.floating-button ::v-deep .active-option {
    background-color: #76b8f1;
    color: white;
}

.floating-button ::v-deep .active-option img {
    opacity: 1;
    filter: brightness(0) invert(1);
}

.floating-button ::v-deep .active-option:hover {
    background-color: #5aa1e6;
}

.floating-button ::v-deep .dropdown-divider {
    margin: 0;
    opacity: 0.1;
}

.floating-button ::v-deep .dropdown-item:disabled {
    opacity: 0.7;
    background-color: #f8f9fa;
    cursor: not-allowed;
}

.floating-button ::v-deep .dropdown-item:disabled:hover {
    transform: none;
    background-color: #f8f9fa;
}

.floating-button ::v-deep .dropdown-item.active-option:disabled {
    background-color: #a8d4f8;
    color: #fff;
}

.right-sidebar-wrapper {
    position: fixed;
    top: calc(3.3rem + 16px);
    right: 0;
    height: calc(100vh - 3.3rem - 16px);
    z-index: 1000;
}
.right-sidebar.sidebar-wide {
    width: 90vw;
    max-width: 1000px;
    min-width: 500px;
}
.right-sidebar {
    position: fixed;
    top: calc(3.3rem + 16px);
    right: 0;
    width: 46%;
    max-width: 500px;
    min-width: 300px;
    transition: 
        transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1),
        width 0.4s cubic-bezier(0.25, 0.8, 0.25, 1),
        max-width 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
    height: calc(100vh - 3.3rem - 24px);
    background-color: white;
    box-shadow: -4px 0 8px rgba(0, 0, 0, 0.15);
    transform: translateX(calc(100% - 24px));
    border-radius: 12px 0 0 12px;
    border-left: 2px solid #76b8f1;
}

.right-sidebar.sidebar-expanded {
    transform: translateX(0);
}

.sidebar-toggle {
    position: absolute;
    left: -32px;
    top: 50%;
    transform: translateY(-50%);
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background-color: #76b8f1;
    border: 2px solid white;
    border-radius: 50%;
    box-shadow: -2px 0 5px rgba(0, 0, 0, 0.2);
    transition: all 0.3s ease;
    z-index: 1001;
    animation: pulseAttention 3s infinite;
}

.sidebar-toggle.active {
    background-color: #5aa1e6;
    animation: none;
    transform: translateY(-50%) scale(1.08);
}

.sidebar-toggle img {
    width: 24px;
    height: 24px;
    filter: none;
}

.sidebar-content {
    padding: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    margin-left: 20px;
}
.sidebar-header {
    background-color: white;
    padding: 1rem;
    margin: 0 0 0 -20px;
    text-align: end;
    border-radius: 12px 0 0 0;
    transition: background-color 0.3s ease;
    border: 2px solid #ffffff00;
}

.sidebar-header h4 {
    color: #76b8f1;
    margin: 0;
    font-weight: 500;
    transition: color 0.3s ease;
}

.right-sidebar.sidebar-expanded .sidebar-header {
    background-color: #76b8f1;
    border: 2px solid #76b8f1;
}

.right-sidebar.sidebar-expanded .sidebar-header h4 {
    color: white;
}

.sidebar-body {
    padding: 0.8rem 0.8rem 0.1rem 0.1rem;
    flex: 1;
    overflow-y: auto;
}

@keyframes pulseAttention {
    0% {
        box-shadow: -2px 0 5px rgba(0, 0, 0, 0.2);
        transform: translateY(-50%) scale(1);
    }
    50% {
        box-shadow: 0 0 20px rgba(118, 184, 241, 0.8);
        transform: translateY(-50%) scale(1.08);
    }
    100% {
        box-shadow: -2px 0 5px rgba(0, 0, 0, 0.2);
        transform: translateY(-50%) scale(1);
    }
}

.right-sidebar.sidebar-expanded .sidebar-toggle {
    animation: none;
}

.dropdown-lang {
    padding: 0;
    margin: 0;
}

.navbar-btn :deep(.img-user) {
    filter: brightness(0) invert(0);
}

.disabled-icon {
    opacity: 0.5;
}

.navigation-arrows {
    margin-right: 1rem;
    gap: 0.5rem;
    display: flex;
    align-items: center;
}

.activity-counter {
    min-width: 40px;
    text-align: center;
    font-weight: 500;
    color: #232323;
    margin: 0 0.5rem;
}

.nav-button {
    padding: 0.25rem;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    box-shadow: none;
}

.nav-button:not(:disabled) {
    opacity: 1;
}

.nav-button:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.nav-icon {
    width: 24px;
    height: 24px;
    filter: brightness(0);
}

.nav-button:hover .nav-icon {
    filter: brightness(0) opacity(0.7);
}

.nav-button:disabled .nav-icon {
    filter: brightness(0) opacity(0.3);
}

.action-button {
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    padding: 0 1rem;
    border-radius: 4px;
    transition: all 0.3s ease;
}

.action-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.status-text {
    color: #232323;
    font-weight: 500;
    font-size: 0.9rem;
}

.feedback-toggle {
    top: calc(50% + 60px);
}

.form-group {
    margin-bottom: 1rem;
    text-align: left;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    color: #232323;
}

.swal2-input, .swal2-textarea {
    width: 100%;
    margin: 0.5rem 0;
}

.swal2-textarea {
    min-height: 100px;
}

.feedback-section {
    padding: 1rem;
}

.feedback-section h5 {
    color: #76b8f1;
    margin-bottom: 1.5rem;
    font-weight: 500;
}

.feedback-actions {
    display: flex;
    gap: 1rem;
    margin-top: 1.5rem;
}

.form-control {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    margin-bottom: 1rem;
}

.form-control:focus {
    border-color: #76b8f1;
    outline: none;
    box-shadow: 0 0 0 2px rgba(118, 184, 241, 0.2);
}

.score-display, .feedback-display {
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: #f8f9fa;
    min-height: 38px;
}

.feedback-display {
    min-height: 100px;
    white-space: pre-wrap;
}

.action-buttons-container {
    position: fixed;
    bottom: 30px;
    right: 30px;
    display: flex;
    align-items: center;
    gap: 20px;
    z-index: 1000;
}
.close-sidebar-btn {
    border: none;
    box-shadow: none;
    line-height: 1;
    color: white;
}
.close-sidebar-btn:hover {
    color: white;
    transform: scale(1.1)
}
.undo-icon {
  cursor: pointer;
}
.icon-flipped {
  transform: scaleX(-1);
  cursor: pointer
}
.disabled-icon {
  cursor: not-allowed;
  opacity: 0.5;
}
</style>